/**
 * Music Bingo — the GUEST's half of the card maths.
 *
 * ⚠ COPIED VERBATIM from muuzbox-reports-admin/src/services/bingoService.js
 * (its pure functions only — the host-side pool builder and callTrack live
 * there and are not needed here). These MUST stay byte-identical: the guest
 * derives their card from (uid, roundId, pool) and the HOST re-derives the
 * same card to verify a BINGO claim. Any drift and a legitimate claim gets
 * rejected. If you change one, change both.
 *
 * No Firebase import at all — every function here is pure.
 */

export const BINGO_PATTERNS = {
  line: { label: 'Single line', points: 10 },
  twoLines: { label: 'Two lines', points: 15 },
  fullHouse: { label: 'Full house', points: 25 },
};

/**
 * 4×4 — 16 squares, chosen so each square is big enough to read a song title
 * on a phone in a dark venue (a 5×5 grid squeezed them too small).
 *
 * NO FREE SQUARE: a free centre needs a true centre cell, which an even grid
 * doesn't have — putting one off-centre reads as a bug. The difficulty works
 * out about the same: a 4-square line here matches a 5×5 line drawn through
 * its free centre.
 *
 * ⚠ If the venue prints PAPER cards, they must be 4×4 too — the app and the
 * paper have to be the same game.
 */
export const CARD_SIZE = 4;
export const FREE_INDEX = -1; // no free square on an even grid

// ── Deterministic shuffle (xorshift32 seeded by uid+roundId) ──────────
const seedFrom = (str) => {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h || 1;
};

const makeRng = (seed) => {
  let s = seed;
  return () => {
    s ^= (s << 13) & 0xffffffff; s >>>= 0;
    s ^= s >>> 17;
    s ^= (s << 5) & 0xffffffff; s >>>= 0;
    return s / 0x100000000;
  };
};

/**
 * The player's card: 16 cells drawn from the pool.
 * Same (uid, roundId, pool) always yields the same card — never stored.
 */
export const bingoCardFor = (uid, roundId, pool) => {
  const tracks = (pool || []).filter(Boolean);
  if (!tracks.length) return [];
  const rng = makeRng(seedFrom(`${uid}::${roundId}`));
  // Fisher-Yates on a copy, driven by the seeded rng.
  const bag = tracks.slice();
  for (let i = bag.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [bag[i], bag[j]] = [bag[j], bag[i]];
  }
  const need = CARD_SIZE * CARD_SIZE;
  const cells = [];
  for (let i = 0; i < need; i++) {
    // FREE_INDEX < 0 means this grid has no free square at all.
    if (i === FREE_INDEX) { cells.push({ free: true, label: 'FREE' }); continue; }
    // Wrap the bag if the pool is smaller than the card (small venues).
    const t = bag[cells.filter((c) => !c.free).length % bag.length];
    cells.push({ free: false, label: t.label || t, trackKey: trackKeyOf(t) });
  }
  return cells;
};

/** Canonical key for matching a called track to a card cell. */
export const trackKeyOf = (t) => {
  const s = typeof t === 'string' ? t : (t?.label || `${t?.title || ''} ${t?.artist || ''}`);
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, '').slice(0, 60);
};

// ── Win detection ─────────────────────────────────────────────────────
const linesOf = (n) => {
  const rows = [];
  const cols = [];
  for (let r = 0; r < n; r++) {
    rows.push(Array.from({ length: n }, (_, c) => r * n + c));
    cols.push(Array.from({ length: n }, (_, c) => c * n + r));
  }
  const d1 = Array.from({ length: n }, (_, i) => i * n + i);
  const d2 = Array.from({ length: n }, (_, i) => i * n + (n - 1 - i));
  return [...rows, ...cols, d1, d2];
};

/**
 * How many complete lines the card has, given the called set. A cell counts
 * only if it's FREE or its track was genuinely called — that's what makes a
 * claim verifiable rather than trusted.
 */
export const completedLines = (cells, calledKeys) => {
  const called = new Set(calledKeys || []);
  const hit = (i) => cells[i] && (cells[i].free || called.has(cells[i].trackKey));
  return linesOf(CARD_SIZE).filter((line) => line.every(hit)).length;
};

export const isFullHouse = (cells, calledKeys) => {
  const called = new Set(calledKeys || []);
  return cells.length > 0 && cells.every((c) => c.free || called.has(c.trackKey));
};

/** Does this card satisfy the round's pattern? The host's verdict comes from here. */
export const verifyClaim = (cells, calledKeys, pattern) => {
  if (pattern === 'fullHouse') return isFullHouse(cells, calledKeys);
  const lines = completedLines(cells, calledKeys);
  if (pattern === 'twoLines') return lines >= 2;
  return lines >= 1;
};
